// Tier 3 (custom): a glass panel floating over the map, which antd's Drawer and Card are not.
// Catalogue: "Side panel (glass)".
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Badge, Button, ConfigProvider, Tag, Tooltip } from 'antd';
import { IoClose, IoTrashOutline } from 'react-icons/io5';
import styled from 'styled-components';

import { mapPanelTheme } from '@config/antdTheme';
import { isSimilarityMock } from '@utils/map/similarity/similaritySource';

// Top right under the navbar, clear of the toolbar and map controls at the bottom; the label column
// of the rows. In pixels.
export const LAYOUT = { top: 78, right: 8, width: 404, bottomClearance: 160, labelWidth: 84 };

const Panel = styled.aside`
	position: fixed;
	top: ${LAYOUT.top}px;
	right: ${LAYOUT.right}px;
	z-index: 20;
	width: ${LAYOUT.width}px;
	max-height: calc(100vh - ${LAYOUT.top + LAYOUT.bottomClearance}px);
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	padding: ${({ theme }) =>
		`${theme.token.paddingLG}px ${theme.token.paddingLG}px ${theme.token.paddingMD}px`};
	font-family: ${({ theme }) => theme.token.fontFamily};
	font-size: ${({ theme }) => theme.token.fontSize}px;
	color: ${({ theme }) => theme.token.colorText};
	background: ${({ theme }) => theme.token.panelGlassBg};
	backdrop-filter: blur(${({ theme }) => theme.token.sizeSM}px);
	border: ${({ theme }) => theme.token.lineWidth}px solid
		${({ theme }) => theme.token.colorBorderSecondary};
	/* Soft corners, as the map's other floating panels (reindeer count) */
	border-radius: ${({ theme }) => theme.token.borderRadiusLG * 2.5}px;
	box-shadow: ${({ theme }) => theme.token.boxShadowSecondary};

	/* Inputs and buttons inside follow the round look */
	.ant-input-affix-wrapper,
	.ant-btn:not(.ant-btn-text):not(.ant-btn-link),
	.ant-segmented,
	.ant-segmented-item {
		border-radius: ${({ theme }) => theme.token.controlHeight}px;
	}
`;

const Header = styled.div`
	flex: none;
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.token.paddingXS}px;
	padding-bottom: ${({ theme }) => theme.token.paddingSM}px;
`;

const Title = styled.h2`
	flex: 1;
	margin: 0;
	font-size: ${({ theme }) => theme.token.fontSizeHeading3}px;
	font-weight: ${({ theme }) => theme.token.fontWeightStrong};
	line-height: 1.2;
`;

// A pill, not a square tag
const Pill = styled(Tag)`
	&& {
		margin: 0;
		padding-inline: ${({ theme }) => theme.token.paddingSM}px;
		border-radius: ${({ theme }) => theme.token.controlHeight}px;
		font-size: ${({ theme }) => theme.token.fontSizeSM}px;
		line-height: ${({ theme }) => theme.token.controlHeightSM}px;
	}
`;

// One line of the panel: a muted label in a fixed column, its controls or values beside it, and a
// rule above. Labels and values line up down the panel, and sit centred between the rules.
const RowGrid = styled.div`
	/* The panel scrolls; its rows keep their height */
	flex: none;
	display: grid;
	grid-template-columns: ${LAYOUT.labelWidth}px minmax(0, 1fr);
	align-items: center;
	column-gap: ${({ theme }) => theme.token.paddingSM}px;
	min-height: ${({ theme }) => theme.token.controlHeightLG + theme.token.paddingSM * 2}px;
	padding: ${({ theme }) => theme.token.paddingSM}px 0;
	border-top: ${({ theme }) => theme.token.lineWidth}px solid
		${({ theme }) => theme.token.colorSplit};
`;

const RowLabel = styled.div`
	font-size: ${({ theme }) => theme.token.fontSizeSM}px;
	color: ${({ theme }) => theme.token.colorTextSecondary};
`;

const RowValue = styled.div`
	grid-column: ${({ $wide }) => ($wide ? '1 / -1' : 'auto')};
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.token.paddingXS}px;
	min-width: 0;
`;

/**
 * A row of the Find panel. Without a label its content spans both columns (a primary button).
 * @param {Object} props
 * @param {React.ReactNode} [props.label]
 */
export const PanelRow = ({ label, children }) => (
	<RowGrid>
		{label && <RowLabel>{label}</RowLabel>}
		<RowValue $wide={!label}>{children}</RowValue>
	</RowGrid>
);

// A muted line under a row's controls
export const Note = styled.div`
	font-size: ${({ theme }) => theme.token.fontSizeSM}px;
	color: ${({ theme }) => theme.token.colorTextSecondary};
`;

// A row's line of controls side by side: never wrapped, the first one takes the room left
export const Line = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${({ theme }) => theme.token.paddingXS}px;
	white-space: nowrap;
`;

/**
 * Where finding runs, said as speed rather than infrastructure: the GPU worker is "fast", this
 * computer "slow".
 * @param {Object|undefined} session - From `GET …/find/session`
 * @returns {{ text: string, status: string, tip: string }}
 */
export const sessionStatus = session => {
	if (!session) return null;
	if (session.backend === 'local') {
		return {
			text: 'Local compute',
			status: 'warning',
			tip: 'Running on this machine (development): a square takes a few seconds.',
		};
	}
	if (session.state === 'ready') {
		return {
			text: 'Compute ready',
			status: 'success',
			tip: 'Compute is running. It stops itself after 10 minutes without a search, also with this panel open.',
		};
	}
	if (session.state === 'stopping') {
		return {
			text: 'Compute stopping',
			status: 'warning',
			tip: 'Compute is stopping (after 10 minutes without a search). It can start again in a few minutes.',
		};
	}
	if (session.state === 'starting') {
		return {
			text: 'Starting compute',
			status: 'processing',
			tip: 'Compute is starting and loading its models: about a minute.',
			starting: true,
		};
	}
	return {
		text: 'Compute off',
		status: 'default',
		// The worker's own failure text names machines and models: the log has it, the page does not
		tip:
			session.state === 'failed'
				? 'Compute could not start. Click to try again.'
				: 'Compute is off. Click to start it.',
		off: true,
	};
};

// "1:07": time since the start was asked for, counting up while compute starts
const Elapsed = ({ since }) => {
	const [now, setNow] = useState(Date.now());
	useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(timer);
	}, []);
	const seconds = Math.max(0, Math.round((now - since) / 1000));
	return ` · ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

/**
 * Compute's status as a pill. It starts compute when it is off (a click, or Enter or Space on it), and
 * counts up while it starts.
 */
const ComputeStatus = ({ session, startRequestedAt, onStart }) => {
	const status = sessionStatus(session);
	if (!status) return null;
	const since = startRequestedAt ?? (session?.started_at ? Date.parse(session.started_at) : null);
	const onKeyDown = event => {
		if (event.key !== 'Enter' && event.key !== ' ') return;
		event.preventDefault();
		onStart();
	};
	return (
		<Tooltip title={status.tip}>
			<Pill
				variant="filled"
				role={status.off ? 'button' : undefined}
				tabIndex={status.off ? 0 : undefined}
				style={status.off ? { cursor: 'pointer' } : undefined}
				onClick={status.off ? onStart : undefined}
				onKeyDown={status.off ? onKeyDown : undefined}>
				<Badge status={status.status} /> {status.text}
				{status.starting && since && <Elapsed since={since} />}
				{status.off && ' · Start'}
			</Pill>
		</Tooltip>
	);
};

/**
 * The Find panel (proof of concept): the header (compute's status, Clear, close) and the rows as
 * children.
 */
const FindPanel = ({ session, startRequestedAt, onStart, onClear, canClear, onClose, children }) =>
	createPortal(
		<ConfigProvider theme={mapPanelTheme}>
			<Panel aria-label="Find">
				<Header>
					<Title>Find</Title>
					{isSimilarityMock && <Pill color="warning">Mock data</Pill>}
					<ComputeStatus
						session={session}
						startRequestedAt={startRequestedAt}
						onStart={onStart}
					/>
					<Tooltip title="Clear the examples and what was found">
						<Button
							type="text"
							icon={<IoTrashOutline />}
							disabled={!canClear}
							onClick={onClear}
							aria-label="Clear"
						/>
					</Tooltip>
					<Button
						type="text"
						icon={<IoClose />}
						aria-label="Close Find"
						onClick={onClose}
					/>
				</Header>
				{children}
			</Panel>
		</ConfigProvider>,
		document.body
	);

export default FindPanel;
